import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Home, Car, ShoppingBag, HeartPulse, Users, PiggyBank, MapPin, Target, Info,
  ArrowRight, Check, TrendingUp, Database, FolderKanban, Briefcase, Compass,
  Lightbulb, GraduationCap, MessageSquareWarning, Sparkles, BarChart3, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString()}` : '-');

const STATE_NAMES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];

const BUCKETS = [
  { label: 'Housing', icon: Home, hex: '#339AE5', keys: ['home'] },
  { label: 'Getting Around', icon: Car, hex: '#29E6F4', keys: ['move'] },
  { label: 'Day-to-Day Living', icon: ShoppingBag, hex: '#8358F5', keys: ['living', 'debt', 'events'] },
  { label: 'Health & Insurance', icon: HeartPulse, hex: '#F05A87', keys: ['health'] },
  { label: 'People Who Depend on You', icon: Users, hex: '#F2C94C', keys: ['people'] },
  { label: 'Savings & Future Goals', icon: PiggyBank, hex: '#2DDBA5', keys: ['future'] },
];

const ACTION_ICON = { database: Database, project: FolderKanban, internship: Briefcase, specialize: Compass };

function ContextItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-cyan-200/70">{label}</p>
      <p className="text-sm font-semibold text-white mt-0.5">{value}</p>
    </div>
  );
}

export default function LifeDesignResults({ res, design, getAuthHeader, controls, onEditLifestyle }) {
  const ctx = design?.context || {};
  const verdict = design?.target_verdict || {};
  const floor = design?.income_floor;
  const soc = verdict.soc_code;
  const homeState = ctx.desired_state || 'New Jersey';
  const city = ctx.desired_city || '';

  const [location, setLocation] = useState(homeState);
  const [remote, setRemote] = useState(false);
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareOn, setCompareOn] = useState(false);
  const [compareState, setCompareState] = useState('North Carolina');
  const [compareComp, setCompareComp] = useState(null);
  const [advisorOpen, setAdvisorOpen] = useState(false);

  const academicOptionsUrl = ctx.institution && ctx.major
    ? `https://www.google.com/search?q=${encodeURIComponent(`${ctx.institution} ${ctx.major} major department`)}`
    : null;

  const fetchComp = useCallback(async (state, useRemote) => {
    if (!soc) { setComp({ available: false }); return null; }
    // Only use the precise metro when the student is looking at their own state.
    const useCity = !useRemote && state === homeState ? city : '';
    const url = `${API}/life-design/compensation?soc_code=${encodeURIComponent(soc)}`
      + `&state=${encodeURIComponent(useRemote ? '' : state)}&city=${encodeURIComponent(useCity)}&remote=${useRemote}`;
    try {
      const r = await axios.get(url, getAuthHeader());
      return r.data;
    } catch { return { available: false }; }
  }, [soc, city, homeState, getAuthHeader]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchComp(location, remote).then((d) => { if (alive) { setComp(d); setLoading(false); } });
    return () => { alive = false; };
  }, [location, remote, fetchComp]);

  useEffect(() => {
    if (!compareOn) { setCompareComp(null); return; }
    let alive = true;
    fetchComp(compareState, false).then((d) => { if (alive) setCompareComp(d); });
    return () => { alive = false; };
  }, [compareOn, compareState, fetchComp]);

  // 6-bucket annual breakdown from the cost model.
  const breakdown = useMemo(() => {
    const per = res?.per_step || [];
    const monthlyOf = (keys) => per.filter((p) => keys.includes(p.key)).reduce((s, p) => s + (p.monthly || 0), 0);
    const rows = BUCKETS.map((b) => ({ ...b, annual: Math.round(monthlyOf(b.keys) * 12) }));
    const total = rows.reduce((s, r) => s + r.annual, 0) || 1;
    return rows.map((r) => ({ ...r, pct: Math.round((r.annual / total) * 100) }));
  }, [res]);

  const align = comp?.alignment;
  const median = comp?.median;
  const cmax = Math.max(median || 0, floor || 0) * 1.12 || 1;

  return (
    <div className="space-y-6" data-testid="life-design-results">
      {/* A. Header + context */}
      <div>
        <div className="flex items-center gap-2 text-[12px] text-cyan-200/70">
          <span>Design My Life</span><ArrowRight className="w-3 h-3" /><span className="text-white">My Results</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-5 mt-2">
          <div>
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight">Your Life Design Results</h1>
            <p className="text-base text-slate-200 mt-2 max-w-xl">A personalized view of how your goals, lifestyle, and career fit together.</p>
          </div>
          {controls}
        </div>
        <div className="glass-tint glass-cyan p-5 mt-5" data-testid="results-context">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <ContextItem label="Institution" value={ctx.institution} />
            <ContextItem label="Major" value={ctx.major} />
            <ContextItem label="Academic Year" value={ctx.academic_year && ctx.academic_year[0].toUpperCase() + ctx.academic_year.slice(1)} />
            <ContextItem label="Career Target" value={ctx.career_target} />
            <ContextItem label="Location Preference" value={location} />
          </div>
          <button onClick={onEditLifestyle} data-testid="edit-my-details-btn"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-100 transition-colors">
            Edit My Details <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* B. The life you designed takes + annual breakdown */}
      <div className="glass-tint glass-emerald fp-rail p-6 md:p-8 grid lg:grid-cols-[minmax(0,0.9fr)_1.1fr] gap-8" data-testid="the-life-you-designed">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-300/85">The life you designed takes</p>
          <p className="font-outfit text-5xl lg:text-6xl font-bold text-white fp-num mt-2" data-testid="results-annual">{money(res?.required_gross)}<span className="text-2xl text-slate-200 font-semibold">/year</span></p>
          <p className="text-base text-slate-200 mt-3">
            {money(res?.monthly_with_savings)} / month <span className="text-slate-300">·</span> ${res?.hourly_equivalent?.toFixed(2)} / hour
          </p>
          <p className="text-sm text-slate-300 mt-4 max-w-sm">Based on your choices for housing, transportation, food, insurance, savings, and the things that matter to you.</p>
          <button onClick={onEditLifestyle} data-testid="edit-lifestyle-btn"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white border border-cyan-400/40 hover:bg-cyan-400/10 transition-colors">
            <Sparkles className="w-4 h-4 text-cyan-300" /> Edit My Lifestyle
          </button>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-white">Your Annual Breakdown</p>
            <Info className="w-4 h-4 text-slate-300" />
          </div>
          <div className="mt-4 space-y-3.5">
            {breakdown.map((b) => (
              <div key={b.label} className="flex items-center gap-3" data-testid={`breakdown-${b.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <b.icon className="w-4 h-4 shrink-0" style={{ color: b.hex }} />
                <span className="w-44 shrink-0 text-sm text-slate-200">{b.label}</span>
                <div className="fp-meter flex-1">
                  <div className="fp-meter-fill" style={{ width: `${Math.max(2, b.pct)}%`, background: b.hex, boxShadow: `0 0 12px ${b.hex}66` }} />
                </div>
                <span className="w-20 text-right text-sm text-white fp-num">{money(b.annual)}</span>
                <span className="w-10 text-right text-sm font-semibold fp-num" style={{ color: b.hex }}>{b.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* C. Can your career support this life? */}
      <div className="glass-tint glass-cyan fp-rail p-6 md:p-7" data-testid="can-career-support">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#29E6F41f', border: '1px solid #29E6F444' }}>
              <Target className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h2 className="font-outfit text-2xl font-semibold text-white">Can Your Career Support This Life?</h2>
              <p className="text-sm text-slate-300 mt-1">Here's how your target career compares in your selected location.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-cyan-200/70 mb-1">Career Target</label>
              <div className="px-3 py-2 rounded-lg bg-slate-900/60 border border-white/15 text-sm text-white font-semibold">{ctx.career_target}</div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-cyan-200/70 mb-1">Location</label>
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900/60 border border-white/15">
                <MapPin className="w-3.5 h-3.5 text-cyan-300" />
                <select value={remote ? '__remote' : location} data-testid="results-location-select"
                  onChange={(e) => { if (e.target.value === '__remote') setRemote(true); else { setRemote(false); setLocation(e.target.value); } }}
                  className="bg-transparent text-sm text-white outline-none">
                  {STATE_NAMES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                  <option value="__remote" className="bg-slate-900">Remote (national)</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer pb-2" data-testid="remote-toggle">
              <input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} className="accent-cyan-400 w-4 h-4" />
              Include remote
            </label>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 h-40 rounded-2xl bg-slate-800/40 animate-pulse" data-testid="comp-loading" />
        ) : !comp?.available ? (
          <div className="mt-6 fp-tile p-5" data-testid="comp-unavailable">
            <p className="text-sm text-slate-200">{comp?.note || 'We could not resolve verified wage data for this target. Set a career target from Explore Careers to see the comparison.'}</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.2fr_1fr] gap-5 mt-6">
            {/* pay in location */}
            <div className="fp-tile p-5" data-testid="career-pay-card">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-slate-200">Career Pay in {remote ? 'Remote roles' : location}</p>
                <Info className="w-3.5 h-3.5 text-slate-300" />
              </div>
              <p className="font-outfit text-4xl font-bold text-white fp-num mt-1">{money(median)}</p>
              <p className="text-sm text-slate-300">median annual pay</p>
              {(comp.entry_range || comp.experienced_range) && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {comp.entry_range && (
                    <div><p className="text-sm text-white fp-num">{money(comp.entry_range[0])} - {money(comp.entry_range[1])}</p><p className="text-[12px] text-slate-300">Entry-level range</p></div>
                  )}
                  {comp.experienced_range && (
                    <div><p className="text-sm text-white fp-num">{money(comp.experienced_range[0])} - {money(comp.experienced_range[1])}</p><p className="text-[12px] text-slate-300">Experienced range</p></div>
                  )}
                </div>
              )}
              <p className="text-[12px] text-slate-300 mt-4">
                {comp.geo_label} · BLS OEWS {comp.data_year}{verdict.represented_by ? ` · represented by ${verdict.represented_by}` : ''}
              </p>
            </div>

            {/* comparison bars */}
            <div className="fp-tile p-5" data-testid="career-vs-lifestyle">
              <p className="text-sm font-semibold text-white">Career vs. Lifestyle Comparison</p>
              <div className="mt-5 space-y-4">
                <div>
                  <div className="flex justify-between text-[12px] text-slate-300 mb-1"><span>Career median pay</span><span className="text-white fp-num">{money(median)}</span></div>
                  <div className="fp-meter"><div className="fp-meter-fill" style={{ width: `${Math.min(100, (median / cmax) * 100)}%`, background: 'linear-gradient(90deg,#29E6F4,#2DDBA5)' }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-[12px] text-slate-300 mb-1"><span>Your lifestyle need</span><span className="text-white fp-num">{money(floor)}</span></div>
                  <div className="fp-meter"><div className="fp-meter-fill" style={{ width: `${Math.min(100, (floor / cmax) * 100)}%`, background: 'linear-gradient(90deg,#339AE5,#8358F5)' }} /></div>
                </div>
              </div>
              <p className="text-[12px] text-slate-300 mt-4 text-right">Break-even: {money(floor)}</p>
            </div>

            {/* verdict */}
            <div className="fp-tile p-5 flex flex-col justify-center" data-testid="alignment-verdict"
              style={{ background: `${align?.hex || '#2DDBA5'}12`, border: `1px solid ${align?.hex || '#2DDBA5'}44` }}>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" style={{ color: align?.hex }} />
                <p className="font-outfit text-lg font-bold leading-tight" style={{ color: align?.hex }} data-testid="alignment-label">{align?.label}</p>
              </div>
              {align?.cushion != null && (
                <p className="font-outfit text-3xl font-bold text-white fp-num mt-3">+{money(align.cushion)}<span className="text-sm text-slate-200 font-medium"> annual cushion</span></p>
              )}
              {align?.gap != null && (
                <p className="font-outfit text-3xl font-bold fp-num mt-3" style={{ color: align.hex }}>{money(align.gap)}<span className="text-sm text-slate-200 font-medium"> estimated annual gap</span></p>
              )}
              <p className="text-[13px] text-slate-200 mt-3">Based on available regional data, {ctx.career_target} in {remote ? 'remote roles' : location} {align?.gap != null ? 'may not fully cover' : 'is likely to support'} the lifestyle you designed. Early-career pay may be lower, so experience and skill-building remain important.</p>
            </div>
          </div>
        )}

        {/* compare another location */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={() => setCompareOn((v) => !v)} data-testid="compare-location-btn"
            className="text-sm text-cyan-300 hover:text-cyan-100 transition-colors inline-flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> {compareOn ? 'Hide comparison' : 'Compare another location'}
          </button>
          {compareOn && (
            <div className="flex items-center gap-3" data-testid="compare-panel">
              <select value={compareState} onChange={(e) => setCompareState(e.target.value)} data-testid="compare-location-select"
                className="bg-slate-900/60 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white outline-none">
                {STATE_NAMES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
              </select>
              {compareComp?.available && (
                <span className="text-sm text-slate-200">{compareState}: <span className="text-white fp-num font-semibold">{money(compareComp.median)}</span> median <span className="text-slate-300">·</span> {compareComp.geo_label}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* D. What this means for you */}
      {comp?.available && comp.what_this_means && (
        <div className="glass-tint glass-violet fp-rail p-6 flex flex-wrap items-start justify-between gap-5" data-testid="what-this-means">
          <div className="flex items-start gap-3 max-w-3xl">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#F2C94C1f', border: '1px solid #F2C94C44' }}>
              <Lightbulb className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h3 className="font-outfit text-lg font-semibold text-white">What This Means for You</h3>
              <p className="text-sm text-slate-200 mt-2 leading-relaxed">{comp.what_this_means}</p>
            </div>
          </div>
          <Link to="/app/career-intelligence" data-testid="view-career-plan-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shrink-0"
            style={{ background: 'linear-gradient(90deg,#29E6F4,#8358F5)', boxShadow: '0 0 18px rgba(41,230,244,.3)' }}>
            View Career Plan <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* E. Strengthen your path */}
      {design?.strengthen_actions?.length > 0 && (
        <div data-testid="strengthen-your-path">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-cyan-300" />
              <div>
                <h3 className="font-outfit text-xl font-semibold text-white">Strengthen Your Path</h3>
                <p className="text-sm text-slate-300">Take steps now to increase your opportunities and long-term earning potential.</p>
              </div>
            </div>
            <Link to="/app/career-intelligence" className="text-sm text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1">View all actions <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {design.strengthen_actions.map((a, i) => {
              const Icon = ACTION_ICON[a.icon] || Compass;
              return (
                <div key={i} className="fp-tile p-5 flex flex-col" data-testid={`strengthen-card-${i}`}>
                  <Icon className="w-6 h-6 text-cyan-300" />
                  <p className="font-outfit text-white font-semibold mt-3 leading-snug">{a.title}</p>
                  <p className="text-[13px] text-slate-300 mt-1.5 flex-1">{a.detail}</p>
                  <Link to={a.path} data-testid={`strengthen-cta-${i}`}
                    className="mt-4 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-cyan-200 border border-cyan-400/30 hover:bg-cyan-400/10 transition-colors">
                    {a.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* F. Other careers */}
      {design?.fit_and_affordable?.length > 0 && (
        <div data-testid="other-careers">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-violet-300" />
              <div>
                <h3 className="font-outfit text-xl font-semibold text-white">Other Careers That Could Support This Life</h3>
                <p className="text-sm text-slate-300">Explore similar careers that align with your interests and may offer different earning potential.</p>
              </div>
            </div>
            <Link to="/app/career-landscape" className="text-sm text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1">View all careers <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {design.fit_and_affordable.slice(0, 3).map((c) => (
              <Link key={c.soc_code + c.title} to={`/app/careers?q=${encodeURIComponent(c.title)}`}
                className="fp-tile p-5 block hover:-translate-y-0.5 transition-transform" data-testid={`other-career-${c.soc_code}`}>
                <p className="font-outfit text-white font-semibold">{c.title}</p>
                <p className="font-outfit text-xl font-bold text-white fp-num mt-2">{money(c.median)}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {c.clears_floor === true && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: '#2DDBA522', color: '#2DDBA5' }}>Covers your life</span>}
                  {c.fit && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: '#29E6F422', color: '#29E6F4' }}>{c.fit} fit</span>}
                  <span className="text-[11px] px-2 py-0.5 rounded-full text-slate-200 border border-white/15">{c.field}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* G. Thinking about a major change */}
      {design?.academic_stage && (
        <div className="glass-tint glass-amber fp-rail p-6 grid lg:grid-cols-2 gap-6" data-testid="major-change">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#8358F51f', border: '1px solid #8358F544' }}>
              <GraduationCap className="w-5 h-5 text-violet-300" />
            </div>
            <div>
              <h3 className="font-outfit text-lg font-semibold text-white">Thinking About a Major Change?</h3>
              <p className="text-sm text-amber-100 mt-2">{design.academic_stage.message}</p>
              <p className="text-sm text-slate-200 mt-3">FuturePath can show options (minor, certificate, or new major), but we recommend talking with your academic advisor before making changes. A program change can affect graduation timing, financial aid, and costs.</p>
              <p className="text-[13px] text-slate-300 mt-3"><span className="text-white font-semibold">Where to start:</span> {design.academic_stage.priority}</p>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-3">
            {academicOptionsUrl ? (
              <a href={academicOptionsUrl} target="_blank" rel="noopener noreferrer" data-testid="explore-academic-options-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(90deg,#29E6F4,#8358F5)' }}>
                Explore {ctx.major} at {ctx.institution} <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <Link to="/app/career-landscape" data-testid="explore-academic-options-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(90deg,#29E6F4,#8358F5)' }}>
                Explore Academic Options <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <button onClick={() => setAdvisorOpen(true)} data-testid="advisor-questions-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-slate-100 border border-white/15 hover:bg-white/5 transition-colors">
              <MessageSquareWarning className="w-4 h-4 text-cyan-300" /> Questions to Ask Your Advisor
            </button>
          </div>
        </div>
      )}

      {/* Advisor questions, shown inline so we never route the student off the page */}
      {advisorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setAdvisorOpen(false)} data-testid="advisor-questions-modal">
          <div className="glass-tint glass-cyan fp-rail p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <MessageSquareWarning className="w-5 h-5 text-cyan-300" />
                <h3 className="font-outfit text-lg font-semibold text-white">Questions to Ask Your Advisor</h3>
              </div>
              <button onClick={() => setAdvisorOpen(false)} data-testid="advisor-modal-close" className="text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-300 mt-2">Take these into your next advising meeting.</p>
            <ul className="mt-4 space-y-2.5">
              {(design.advisor_questions || []).map((q, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-100" data-testid={`advisor-q-${i}`}>
                  <span className="text-cyan-300 font-semibold fp-num">{i + 1}.</span>{q}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* H. Next step */}
      <div className="glass-tint glass-cyan fp-rail p-6 flex flex-wrap items-center justify-between gap-5" data-testid="next-step">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-cyan-300" />
          <div>
            <h3 className="font-outfit text-xl font-semibold text-white">Same goals. More possibilities.</h3>
            <p className="text-sm text-slate-300">Adjust your lifestyle, explore locations, or build new skills. Your future is flexible.</p>
          </div>
        </div>
        <Link to="/app/career-intelligence" data-testid="take-next-step-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-base font-bold text-white"
          style={{ background: 'linear-gradient(90deg,#29E6F4 0%,#339AE5 50%,#8358F5 100%)', boxShadow: '0 0 24px rgba(41,230,244,.35)' }}>
          Take the Next Step <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
